/**
 * ModelRunInfo.tsx
 * لوحة "نموذج التنبؤ" في الإعدادات: أي دورة معروضة الآن، كم عمرها، ومتى يُتوقَّع التحديث
 * القادم مع عدّ تنازلي حيّ — على غرار Windy.
 *
 * كل الأرقام مصدرها meta.json الذي يكتبه الخادم عند كل توليد، فلا شيء هنا مُقدَّر بالحدس
 * سوى موعد التحديث القادم، وهو موسوم صراحةً بأنه تقديري (راجع modelRunInfo.ts).
 */

import { FiClock, FiRefreshCw, FiDatabase, FiAlertTriangle } from 'react-icons/fi';
import { useWeatherStore } from '../../store/weatherStore';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from '../../i18n/LanguageContext';
import { useModelLabels } from '../../i18n/useModelLabels';
import { useModelRunInfo, useNowTick, formatDuration, isRunStale } from '../../services/modelRunInfo';

/** "06Z" — ساعة الدورة بتوقيت UTC، وهو الاصطلاح المتعارف عليه في نشرات النماذج. */
function cycleLabel(hourUtc: number): string {
    return `${String(hourUtc).padStart(2, '0')}Z`;
}

export function ModelRunInfo() {
    const { selectedModel, availableModels } = useWeatherStore(useShallow((s) => ({
        selectedModel: s.selectedModel,
        availableModels: s.availableModels,
    })));
    const { lang, t, locale } = useTranslation();
    const { modelResolution, modelProvider } = useModelLabels();
    const { status, info } = useModelRunInfo(selectedModel);
    const now = useNowTick(status === 'ready');
    const stale = info !== null && isRunStale(info.runEpoch, now, selectedModel);

    const model = availableModels.find((m) => m.id === selectedModel);
    const timeFmt = (epoch: number) =>
        new Date(epoch * 1000).toLocaleString(locale, {
            hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short',
        });

    return (
        <div className="mb-5">
            <h3 className="text-xs font-bold m-0 mb-2.5 opacity-70 uppercase tracking-[0.5px]">{t('settings.model.title')}</h3>

            <div className="bg-white/[0.04] light:bg-black/[0.03] border border-white/[0.06] light:border-[var(--panel-border)] rounded-[10px] px-[14px] py-3">
                <div className="flex items-center flex-wrap gap-2 pb-2.5 border-b border-white/[0.06] light:border-[var(--divider)]">
                    <span className="text-sm font-bold tracking-[0.3px]">{model?.name ?? selectedModel}</span>
                    {model && modelResolution(model) && <span className="text-[11px] font-semibold px-[7px] py-0.5 rounded-full bg-[rgba(255,193,7,0.16)] text-[#ffc107]">{modelResolution(model)}</span>}
                    {model && modelProvider(model) && <span className="text-[11px] opacity-55 ms-auto">{modelProvider(model)}</span>}
                </div>

                {status === 'loading' && <p className="mt-3 mb-0 mx-0 text-[11px] opacity-50 leading-[1.5]">{t('common.loading')}</p>}
                {status === 'missing' && <p className="mt-3 mb-0 mx-0 text-[11px] opacity-50 leading-[1.5]">{t('settings.model.unavailable')}</p>}

                {status === 'ready' && info && (
                    <div className="flex flex-col">
                        <div className="flex justify-between items-start gap-3 py-[9px] border-b border-white/[0.04] light:border-[var(--divider)] last-of-type:border-b-0">
                            <span className="flex items-center gap-[7px] text-[12.5px] opacity-75 whitespace-nowrap"><FiDatabase /> {t('settings.model.cycle')}</span>
                            <span className="flex flex-col items-end gap-0.5 text-[13px] font-semibold text-end">
                                {cycleLabel(info.cycleHourUtc)}
                                <span className="text-[11px] font-medium opacity-55">{timeFmt(info.runEpoch)}</span>
                            </span>
                        </div>

                        <div className="flex justify-between items-start gap-3 py-[9px] border-b border-white/[0.04] light:border-[var(--divider)] last-of-type:border-b-0">
                            <span className="flex items-center gap-[7px] text-[12.5px] opacity-75 whitespace-nowrap"><FiClock /> {t('settings.model.age')}</span>
                            <span className={`flex gap-0.5 text-[13px] font-semibold text-end ${stale ? 'flex-row items-center gap-1.5 text-[#ff9800]' : 'flex-col items-end'}`}>
                                {stale && <FiAlertTriangle />}
                                {t('settings.model.ago', { d: formatDuration(now - info.runEpoch * 1000, lang) })}
                            </span>
                        </div>

                        {info.generatedEpoch !== null && (
                            <div className="flex justify-between items-start gap-3 py-[9px] border-b border-white/[0.04] light:border-[var(--divider)] last-of-type:border-b-0">
                                <span className="flex items-center gap-[7px] text-[12.5px] opacity-75 whitespace-nowrap"><FiRefreshCw /> {t('settings.model.generated')}</span>
                                <span className="flex flex-col items-end gap-0.5 text-[13px] font-semibold text-end">{timeFmt(info.generatedEpoch)}</span>
                            </div>
                        )}

                        {info.nextExpectedEpoch !== null && (
                            <div className="flex justify-between items-start gap-3 py-[9px] border-b border-white/[0.04] light:border-[var(--divider)] last-of-type:border-b-0 [&_span:last-child]:[font-variant-numeric:tabular-nums]">
                                <span className="flex items-center gap-[7px] text-[12.5px] opacity-75 whitespace-nowrap"><FiRefreshCw /> {t('settings.model.next')}</span>
                                <span className="flex flex-col items-end gap-0.5 text-[13px] font-semibold text-end">
                                    {now >= info.nextExpectedEpoch * 1000
                                        ? t('settings.model.due')
                                        : <>
                                            {timeFmt(info.nextExpectedEpoch)}
                                            <span className="text-[11px] font-medium opacity-55">
                                                {t('settings.model.in', { d: formatDuration(info.nextExpectedEpoch * 1000 - now, lang) })}
                                            </span>
                                        </>}
                                </span>
                            </div>
                        )}

                        <p className={`mt-2.5 mb-0 mx-0 text-[11px] leading-[1.5] ${stale ? 'text-[#ff9800] opacity-85' : 'opacity-50'}`}>
                            {stale ? t('settings.model.staleNote') : t('settings.model.note')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
